import { useEffect, useState } from "react";
import { Activity, HardDrive, Loader2, RefreshCw, ShieldCheck } from "lucide-react";
import { api, apiError, shortDate } from "@/lib/api";import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const TONE = { Healthy: "default", Warning: "secondary", Failed: "destructive" };

export default function SystemHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/system-health");
      setData(d);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div data-testid="system-health">
      <PageHeader
        testId="system-health-header"
        title="System Health & Backup"
        subtitle="Backups, files, jobs and integrations at a glance. No secrets are shown."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="gap-1.5" disabled={testing}
              data-testid="run-restore-test"
              onClick={async () => {
                setTesting(true);
                try {
                  const { data: r } = await api.post("/system-health/restore-test");
                  toast.success(`Backup verified — ${r.records_in_backup} records across `
                    + `${r.collections_checked} collections`);
                  load();
                } catch (e) {
                  toast.error(apiError(e));
                } finally {
                  setTesting(false);
                }
              }}>
              {testing ? <Loader2 className="h-4 w-4 animate-spin" />
                : <ShieldCheck className="h-4 w-4" />}
              Run restore test
            </Button>
            <Button variant="outline" className="gap-1.5" onClick={load}
              data-testid="health-refresh">
              <RefreshCw className="h-4 w-4" /> Refresh
            </Button>
          </div>
        }
      />
      <div className="p-4 sm:p-6">
        {loading ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : !data ? null : (
          <>
            <div className="surface mb-4 flex flex-wrap items-center justify-between gap-3 p-4">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Overall</span>
                <Badge variant={TONE[data.overall]} data-testid="health-overall">
                  {data.overall}
                </Badge>
              </div>
              <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                <span><HardDrive className="mr-1 inline h-3.5 w-3.5" />
                  {data.counts.documents} documents • {data.counts.storage_mb} MB</span>
                <span>{data.counts.backups} backups</span>
                <span>{data.counts.audit_entries} audit entries</span>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {data.cards.map((c) => (
                <div key={c.key} className="surface p-4" data-testid={`health-card-${c.key}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium">{c.label}</span>
                    <Badge variant={TONE[c.status]} className="text-[10px]">{c.status}</Badge>
                  </div>
                  <p className="mt-1.5 text-xs text-muted-foreground">{c.detail}</p>
                  {c.at && (
                    <p className="mt-1 text-[10px] text-muted-foreground">{shortDate(c.at)}</p>
                  )}
                </div>
              ))}
            </div>

            <p className="mt-4 text-xs text-muted-foreground" data-testid="health-note">
              {data.note} Migration steps are documented in MIGRATION_RUNBOOK.md — take a fresh
              backup and export your files before moving the ERP to another host.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
